/*
 * GET-PROJECT-SUMMARY.JS - Project Summary Data Management Service
 * 
 * RECENT CHANGES - Hours Calculation Integration
 * @Author Samuel Paintsil
 * @Modified 2025-07-03
 * 
 * SUMMARY:
 * Enhanced project summary service to include forecast hours and actual hours calculations
 * for tasks and stages. Integrates timesheet data with labor forecasting to provide
 * comprehensive hour tracking across project sections.
 * 
 * NEW FEATURES ADDED:
 * 1. Timesheet Data Integration - Fetches approved timesheet entries for actual hours
 * 2. Hours Calculation Enhancement - Calculates forecast and actual hours by task
 * 3. Stage-level Hour Aggregation - Rolls up hours data to section/stage level
 * 4. Enhanced Section Data - Includes hours display formatting for UI
 * 
 * MAJOR FUNCTION MODIFICATIONS:
 * - getProjectSummaryData() - Added timesheet data fetching and hours calculations
 * - Enhanced section data processing with hours aggregation
 * - Updated Redux state to include enhanced section data with hours
 * 
 * DEPENDENCIES ADDED:
 * - getResourceAllocations() - For timesheet data retrieval
 * - calculateTasksHours() - For task-level hours calculation
 * - aggregateHoursByStage() - For stage-level hours aggregation
 * - formatHoursDisplay() - For consistent hours formatting
 */

import {
    addProjectAction,
    addFinancialCardAction,
    addProgressCardAction,
    addOverviewCardsAction,
    addOptionsAction,
    addProjectTotalAction,
    addRevisedProjectTotalAction,
    addSectionsAction,
    addSectionDataAction,
    addOptionDataAction,
    addVariationDataAction,
    addProjectVariationAction,
    addSectionVariationAction,
    addProjectCardAction,
    addLaborDataAction,
    addProgressClaimsAction,
    addOptionsListAction,
    addResourceAllocationsAction
} from "../../../../slice/project-summary";
import {
    getProjectSummary,
    getProgressClaimList,
    getOverviewCounts,
    getOptions,
    saveProjectType,
    getPOList,
} from "../../../../actions/project-management.action";
import idx from "idx";
import _ from "lodash";
import { setCookie } from "../../../../util/auth";
import { stageTotal } from "../../../../util/calculations/calculate-stage";
import { optionsTotal } from "../../../../util/calculations/calculate-option";
import { getRevisedProjectTotal } from "../get-revised-calculation";
import { combineData, getVariationNumber } from "../../../../util/common";
import { getWorkAllocationsData } from "../get-work-allocations";
import { getLaborCost } from "../../../../actions/dashboard.action";
import { getResourceAllocations } from "../../../../actions/timesheet.action";
import {
    formatHoursDisplay,
    getVariance,
    getUtilisationRate,
    getEffortCompletionPercentage,
    getTimesheetHoursMap
} from '../hours-calculation';
import { roundValue, toDecimal } from "../../../../util/decimal-utils";
import { PROJECT_TYPE } from "../../../../util/enums";
import calculateTotalProjectHours from "./helper/calculate-total-project-hours";
import calculateHoursRecursively from "./helper/calculate-hours-recursively";
import attachSubtasksToTasks from "./helper/attach-subtasks-to-tasks";
import calculateVariationHours from "./helper/calculate-variation-hours";
import { calculateActualBudget } from "./helper/calculate-actual-budget";
import { getLevels } from "./helper/get-levels";

let inFlightSummaryPromise = null;
let inFlightSummaryKey = null;

/**
 * Fetches and aggregates project summary data including options, financials, and resource allocations.
 * Reuses existing Redux project state if forceFresh is false and projectId matches.
 *
 * @param {Object} params - Parameters object
 * @param {string} params.projectId - Unique project identifier
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {string|null} [params.type=null] - Project view/data type
 * @param {boolean} [params.forceFresh=false] - Whether to bypass Redux cache and force fresh fetch
 * @returns {Promise<Object|undefined>} Aggregated summary data object
 */
export const getProjectSummaryData = async ({ projectId, dispatch, type = null, forceFresh = false }) => {
    if (!projectId) return;

    const cacheKey = `${projectId}_${type || 'default'}`;

    if (!forceFresh && inFlightSummaryPromise && inFlightSummaryKey === cacheKey) {
        return inFlightSummaryPromise;
    }

    inFlightSummaryKey = cacheKey;
    const fetchPromise = (async () => {
        let totalApprovedInvoices = 0;
        let totalClaimAmount = 0, totalRetentionAmount = 0;
        let showStageSelect = false;


        let overviewCards = {
            budgetCount: 0,
            purchaseOrderCount: 0,
            workOrderCount: 0,
            invoiceCount: 0,
            vendorInvoiceCount: 0,
            variationCount: 0,
            totalRFQSend: 0,
        }



        setCookie({ name: "projectId", value: projectId });

        const isTender = type === PROJECT_TYPE.TENDER;
        const isVariation = type === 'variation';
        const isBudget = type === 'budget';

        let progressClaims = [],
            laborData = {},
            timesheetData = [],
            subtasksData = [],
            overviewCountsData = {},
            optionList = [],
            workAllocations = {},
            workOrdersList = [];

        if (isTender || isVariation) {
            [optionList] = await Promise.all([
                dispatch(getOptions(projectId)),
            ]);
        } else if (isBudget) {
            const timesheetPromise = dispatch(getResourceAllocations({ projectId }));
            let workOrdersData;
            [
                progressClaims,
                laborData,
                timesheetData,
                optionList,
                workAllocations,
                workOrdersData
            ] = await Promise.all([
                dispatch(getProgressClaimList(projectId)),
                dispatch(getLaborCost({ id: projectId, skipDashboardData: true })),
                timesheetPromise,
                dispatch(getOptions(projectId, { type: 'budget' })),
                getWorkAllocationsData({ projectId, dispatch, timesheetData: timesheetPromise }),
                dispatch(getPOList({ projectId, vendorType: "sub-contractor", limit: 1000 })).catch(() => ({ list: [] }))
            ]);
            workOrdersList = workOrdersData?.list || [];
        } else {
            const timesheetPromise = dispatch(getResourceAllocations({ projectId }));
            let workOrdersData;
            [
                progressClaims,
                laborData,
                timesheetData,
                overviewCountsData,
                optionList,
                workAllocations,
                workOrdersData
            ] = await Promise.all([
                dispatch(getProgressClaimList(projectId)),
                dispatch(getLaborCost({ id: projectId, skipDashboardData: true })),
                timesheetPromise,
                dispatch(getOverviewCounts(projectId)),
                dispatch(getOptions(projectId, type ? { type } : {})),
                getWorkAllocationsData({ projectId, dispatch, timesheetData: timesheetPromise }),
                dispatch(getPOList({ projectId, vendorType: "sub-contractor", limit: 1000 })).catch(() => ({ list: [] }))
            ]);
            workOrdersList = workOrdersData?.list || [];
        }

        const purchaseOrdersCount = overviewCountsData?.purchaseOrdersCount || 0;
        const workOrdersCount = overviewCountsData?.workOrdersCount || workOrdersList.filter(wo => {
            const status = (wo.status || '').toLowerCase();
            return status !== 'cancelled' && status !== 'canceled';
        }).length;
        const vendorInvoiceCount = overviewCountsData?.vendorInvoiceCount || 0;
        const invoiceCount = overviewCountsData?.invoiceCount || 0;
        const variationCount = Array.isArray(optionList) ? optionList.length : 0;
        const { timeSpentProgress, timeAllocated } = workAllocations || { timeSpentProgress: 0, timeAllocated: 0 };

        progressClaims.forEach((claims) => {
            if (claims.progressClaimStatus && claims.progressClaimStatus != 'Draft') {
                const claimVal = claims.claimAmount ?? claims.claim_amount ?? claims.total_claim_amount ?? 0;
                totalClaimAmount = toDecimal(totalClaimAmount).plus(toDecimal(claimVal)).toNumber();
                totalRetentionAmount = toDecimal(totalRetentionAmount).plus(toDecimal(claims.retentionAmount || 0).minus(toDecimal(claims.retentionClaimAmount || 0))).toNumber();
                totalApprovedInvoices++;
            }
        });

        overviewCards.budgetCount = (Array.isArray(progressClaims) && progressClaims.length) || 0;
        overviewCards.invoiceCount = invoiceCount;
        overviewCards.purchaseOrderCount = purchaseOrdersCount;
        overviewCards.workOrderCount = workOrdersCount;
        overviewCards.vendorInvoiceCount = vendorInvoiceCount;
        overviewCards.variationCount = variationCount;
        overviewCards.totalRFQSend = 0;

        const laborCost = idx(laborData, _ => _.taskData) || {};
        let resourceActualSpending = Object.values(laborCost).reduce((acc, value) => toDecimal(acc).plus(toDecimal(value)).toNumber(), 0);

        const timesheetEntries = Array.isArray(timesheetData) ? timesheetData : [];
        const subtasks = Array.isArray(subtasksData) ? subtasksData : [];

        return dispatch(getProjectSummary(projectId)).then((projectData) => {
            // Save the project type to Redux and cookies
            if (projectData.projectType) {
                dispatch(saveProjectType(projectData.projectType));
                setCookie({ name: "projectType", value: projectData.projectType });
            }

            getLevels({ levelId: projectData.level, dispatch });
            // 0. Extract variation and option data
            const isProjectView = type === 'project-view';
            const optionData = getVariationNumber(optionList);
            const variationData = isProjectView
                ? optionData.filter(_ => _.proposal_status === 'APPROVED')
                : optionData.filter(_ => !['REJECTED', 'CANCELLED'].includes(_.proposal_status));
            const projectLevelVariation = variationData.filter(_ => !_.stageId);
            const sectionLevelVariation = variationData.filter(_ => _.stageId);

            // 1. Apply hours calculation to all variations FIRST to ensure totals are accurate
            const enhancedVariationData = calculateVariationHours(variationData, timesheetEntries, subtasks);
            const enhancedOptionData = calculateVariationHours(optionData, timesheetEntries, subtasks);
            const enhancedProjectLevelVariation = calculateVariationHours(projectLevelVariation, timesheetEntries, subtasks);
            const enhancedSectionLevelVariation = calculateVariationHours(sectionLevelVariation, timesheetEntries, subtasks);

            // 2. Now calculate option totals with enhanced hours data
            const optionCalculation = optionsTotal({ options: enhancedOptionData, projectType: projectData.type });
            const approvedOptionCalculation = isProjectView
                ? optionsTotal({ options: enhancedOptionData.filter(_ => _.proposal_status === 'APPROVED'), projectType: projectData.type })
                : optionCalculation;

            // 3. Add hours display formatting for the Variation Total row in UI
            if (optionCalculation && optionCalculation.projectTotal) {
                optionCalculation.projectTotal.forecastHoursDisplay = formatHoursDisplay(optionCalculation.projectTotal.forecastHours);
                optionCalculation.projectTotal.actualHoursDisplay = formatHoursDisplay(optionCalculation.projectTotal.actualHours);
                optionCalculation.projectTotal.variance = getVariance(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
                optionCalculation.projectTotal.utilisationRate = getUtilisationRate(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
                optionCalculation.projectTotal.effortCompletionPercentage = getEffortCompletionPercentage(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
            }
            if (approvedOptionCalculation && approvedOptionCalculation.projectTotal && approvedOptionCalculation !== optionCalculation) {
                approvedOptionCalculation.projectTotal.forecastHoursDisplay = formatHoursDisplay(approvedOptionCalculation.projectTotal.forecastHours);
                approvedOptionCalculation.projectTotal.actualHoursDisplay = formatHoursDisplay(approvedOptionCalculation.projectTotal.actualHours);
                approvedOptionCalculation.projectTotal.variance = getVariance(approvedOptionCalculation.projectTotal.actualHours, approvedOptionCalculation.projectTotal.forecastHours);
                approvedOptionCalculation.projectTotal.utilisationRate = getUtilisationRate(approvedOptionCalculation.projectTotal.actualHours, approvedOptionCalculation.projectTotal.forecastHours);
                approvedOptionCalculation.projectTotal.effortCompletionPercentage = getEffortCompletionPercentage(approvedOptionCalculation.projectTotal.actualHours, approvedOptionCalculation.projectTotal.forecastHours);
            }

            let stages = idx(projectData, _ => _.stages) || [];
            const sectionData = combineData({ stages, variation: enhancedVariationData }) || [];

            const projectcard = {
                projectName: projectData.name,
                address: projectData.address,
                addressBreakdown: projectData.addressBreakdown,
                customer: projectData.customer ? projectData.customer.businessName : '',
                contact: [idx(projectData, _ => _.customerContact.firstName), idx(projectData, _ => _.customerContact.lastName)].filter(item => item).join(' '),
                assignedTo: projectData.manager ? projectData.manager.name : '',
            };
            const progresscard = {
                materialAllowance: 0,
                resourceAllowance: 0,
                contractorAllowance: 0,
                remainingMaterialBudget: 0,
                remainingResourceBudget: 0,
                remainingContractorBudget: 0,
                materialActualSpending: 0,
                resourceActualSpending: resourceActualSpending,
                contractorActualSpending: 0,
                timeSpend: timeSpentProgress,
                timeAllocated: timeAllocated,
            };
            const financialCard = {
                contract: 0.00,
                variations: 0.00,
                revisedContract: 0.00,
                claimed: 0.00,
                retentionHeld: 0.00,
                balance: 0.00,
            };

            const processStages = (stagesData) => {
                if (Array.isArray(stagesData)) {
                    stagesData.forEach((stage) => {
                        if (idx(stage, _ => _.stages.length)) {
                            processStages(stage.stages);
                        }
                        if (idx(stage, _ => _.tasks.length)) {
                            stage.tasks.forEach((task) => {
                                (task.material || []).forEach((material, mIndex) => {
                                    if (typeof material === 'string') {
                                        task.material[mIndex] = JSON.parse(material);
                                    }
                                });
                            });
                        }
                    });
                }
            };

            const stageData = stageTotal(sectionData);
            let revisedProjectTotal = getRevisedProjectTotal({ sectionProjectTotal: stageData.projectTotal, optionProjectTotal: approvedOptionCalculation.projectTotal });

            const sectionDataWithSubtasks = attachSubtasksToTasks(sectionData, subtasks);
            const hoursMap = getTimesheetHoursMap(timesheetEntries);
            const enhancedSectionData = calculateHoursRecursively(sectionDataWithSubtasks, timesheetEntries, hoursMap);

            // Map recursive hour calculations back to the summarized sections object
            const mapHoursToSections = (sections, enhancedStages) => {
                enhancedStages.forEach(enhancedStage => {
                    if (sections[enhancedStage.id]) {
                        sections[enhancedStage.id] = {
                            ...sections[enhancedStage.id],
                            forecastHours: enhancedStage.forecastHours,
                            actualHours: enhancedStage.actualHours,
                            forecastHoursDisplay: enhancedStage.forecastHoursDisplay,
                            actualHoursDisplay: enhancedStage.actualHoursDisplay,
                            variance: enhancedStage.variance,
                            utilisationRate: enhancedStage.utilisationRate,
                            effortCompletionPercentageValue: enhancedStage.effortCompletionPercentageValue,
                            effortCompletionPercentage: enhancedStage.effortCompletionPercentage,
                            tasks: enhancedStage.tasks || []
                        };
                    }
                    if (enhancedStage.stages && enhancedStage.stages.length > 0) {
                        mapHoursToSections(sections, enhancedStage.stages);
                    }
                });
            };
            mapHoursToSections(stageData.sections, enhancedSectionData);

            // 1. Build a global tasks hours map from the recursive calculation results
            const globalTaskHoursMap = {};
            const buildGlobalTaskHoursMap = (enhancedStages) => {
                enhancedStages.forEach(stage => {
                    if (stage.tasks && Array.isArray(stage.tasks)) {
                        stage.tasks.forEach(task => {
                            globalTaskHoursMap[task.id] = {
                                forecastHours: task.forecastHours,
                                actualHours: task.actualHours,
                                forecastHoursDisplay: task.forecastHoursDisplay,
                                actualHoursDisplay: task.actualHoursDisplay,
                                variance: task.variance,
                                utilisationRate: task.utilisationRate,
                                effortCompletionPercentageValue: task.effortCompletionPercentageValue,
                                effortCompletionPercentage: task.effortCompletionPercentage,
                            };
                        });
                    }
                    if (stage.stages && stage.stages.length > 0) {
                        buildGlobalTaskHoursMap(stage.stages);
                    }
                });
            };
            buildGlobalTaskHoursMap(enhancedSectionData);

            // 2. Enrich individual tasks stored within each section's taskList
            Object.keys(stageData.sections).forEach(sectionId => {
                const section = stageData.sections[sectionId];
                if (section.taskList && section.taskList.tasks) {
                    Object.keys(section.taskList.tasks).forEach(taskId => {
                        if (globalTaskHoursMap[taskId]) {
                            section.taskList.tasks[taskId] = {
                                ...section.taskList.tasks[taskId],
                                ...globalTaskHoursMap[taskId]
                            };
                        }
                    });
                }
            });

            // --- Start of additions for projectTotal and revisedProjectTotal hours ---
            const { plannedEffort, actualHours } = calculateTotalProjectHours(enhancedSectionData);
            let totalProjectPlannedEffort = plannedEffort;
            let totalProjectActualHours = actualHours;

            const projectTotalVariance = getVariance(totalProjectActualHours, totalProjectPlannedEffort);
            const projectTotalUtilisationRate = getUtilisationRate(totalProjectActualHours, totalProjectPlannedEffort);
            const projectTotalEffortCompletionPercentage = getEffortCompletionPercentage(totalProjectActualHours, totalProjectPlannedEffort);

            // Update projectTotal with hours
            stageData.projectTotal = {
                ...stageData.projectTotal,
                forecastHours: totalProjectPlannedEffort,
                actualHours: totalProjectActualHours,
                forecastHoursDisplay: formatHoursDisplay(totalProjectPlannedEffort),
                actualHoursDisplay: formatHoursDisplay(totalProjectActualHours),
                variance: roundValue(projectTotalVariance, 0),
                utilisationRate: roundValue(projectTotalUtilisationRate, 0),
                effortCompletionPercentageValue: projectTotalEffortCompletionPercentage,
                effortCompletionPercentage: `${projectTotalEffortCompletionPercentage.toFixed(2)}%`,
            };

            // Recalculate projectTotal's percentageComplete based on Financials (Total Claimed / Contract),
            // mirroring the revisedProjectTotal fix below, so the "Project Total" row (which reads from
            // stageData.projectTotal, not revisedProjectTotal) also reflects approved progress claims.
            if (toDecimal(stageData.projectTotal.totalSaleAmount).gt(0)) {
                const projectTotalFinancialPercentage = roundValue(
                    toDecimal(totalClaimAmount).dividedBy(toDecimal(stageData.projectTotal.totalSaleAmount)).times(100)
                );
                stageData.projectTotal.percentageComplete = projectTotalFinancialPercentage;
                stageData.projectTotal.completionPercentage = `${roundValue(projectTotalFinancialPercentage, 0)}%`;
            }

            // Enrich variation hours into option stage summary and project total
            if (optionCalculation) {
                // 1. Enrich stage summaries (Variation Total rows)
                if (optionCalculation.stageSummary) {
                    Object.keys(optionCalculation.stageSummary).forEach(stageId => {
                        const summary = optionCalculation.stageSummary[stageId];
                        const f = summary.forecastHours || 0;
                        const a = summary.actualHours || 0;
                        const v = getVariance(a, f);
                        const u = getUtilisationRate(a, f);
                        const c = getEffortCompletionPercentage(a, f);

                        optionCalculation.stageSummary[stageId] = {
                            ...summary,
                            forecastHoursDisplay: formatHoursDisplay(f),
                            actualHoursDisplay: formatHoursDisplay(a),
                            variance: roundValue(v, 0),
                            utilisationRate: roundValue(u, 0),
                            effortCompletionPercentageValue: c,
                            effortCompletionPercentage: `${c.toFixed(2)}%`,
                        };

                        // 2. Enrich individual variations within this stage's optionList (Object map)
                        if (summary.optionList && typeof summary.optionList === 'object') {
                            Object.keys(summary.optionList).forEach(optionId => {
                                const variationItem = summary.optionList[optionId];
                                const enhancedV = enhancedVariationData.find(v => String(v.id) === String(optionId));
                                if (enhancedV) {
                                    Object.assign(variationItem, {
                                        forecastHours: enhancedV.forecastHours,
                                        actualHours: enhancedV.actualHours,
                                        forecastHoursDisplay: enhancedV.forecastHoursDisplay,
                                        actualHoursDisplay: enhancedV.actualHoursDisplay,
                                        variance: enhancedV.variance,
                                        utilisationRate: enhancedV.utilisationRate,
                                        effortCompletionPercentageValue: enhancedV.effortCompletionPercentageValue,
                                        effortCompletionPercentage: enhancedV.effortCompletionPercentage,
                                    });
                                }
                            });
                        }
                    });
                }

                // 3. Enrich overall options project total
                const of = optionCalculation.projectTotal?.forecastHours || 0;
                const oa = optionCalculation.projectTotal?.actualHours || 0;
                const ov = getVariance(oa, of);
                const ou = getUtilisationRate(oa, of);
                const oc = getEffortCompletionPercentage(oa, of);

                optionCalculation.projectTotal = {
                    ...optionCalculation.projectTotal,
                    forecastHoursDisplay: formatHoursDisplay(of),
                    actualHoursDisplay: formatHoursDisplay(oa),
                    variance: roundValue(ov, 0),
                    utilisationRate: roundValue(ou, 0),
                    effortCompletionPercentageValue: oc,
                    effortCompletionPercentage: `${oc.toFixed(2)}%`,
                };

                if (approvedOptionCalculation && approvedOptionCalculation.projectTotal && approvedOptionCalculation !== optionCalculation) {
                    const aof = approvedOptionCalculation.projectTotal?.forecastHours || 0;
                    const aoa = approvedOptionCalculation.projectTotal?.actualHours || 0;
                    const aov = getVariance(aoa, aof);
                    const aou = getUtilisationRate(aoa, aof);
                    const aoc = getEffortCompletionPercentage(aoa, aof);

                    approvedOptionCalculation.projectTotal = {
                        ...approvedOptionCalculation.projectTotal,
                        forecastHoursDisplay: formatHoursDisplay(aof),
                        actualHoursDisplay: formatHoursDisplay(aoa),
                        variance: roundValue(aov, 0),
                        utilisationRate: roundValue(aou, 0),
                        effortCompletionPercentageValue: aoc,
                        effortCompletionPercentage: `${aoc.toFixed(2)}%`,
                    };
                }

                if (isProjectView && approvedOptionCalculation) {
                    optionCalculation.approvedProjectTotal = approvedOptionCalculation.projectTotal;
                }
            }

            // Add hours from ALL variations to totals for the Revised Project Total
            let finalPlannedEffort = toDecimal(totalProjectPlannedEffort);
            let finalActualHours = toDecimal(totalProjectActualHours);

            enhancedVariationData.forEach(variation => {
                finalPlannedEffort = finalPlannedEffort.plus(toDecimal(variation.forecastHours || 0));
                finalActualHours = finalActualHours.plus(toDecimal(variation.actualHours || 0));
            });

            const revisedProjectTotalVariance = getVariance(finalActualHours.toNumber(), finalPlannedEffort.toNumber());
            const revisedProjectTotalUtilisationRate = getUtilisationRate(finalActualHours.toNumber(), finalPlannedEffort.toNumber());
            const revisedProjectTotalEffortCompletionPercentage = getEffortCompletionPercentage(finalActualHours.toNumber(), finalPlannedEffort.toNumber());

            // Update revisedProjectTotal with summed hours from both project and variations
            revisedProjectTotal = {
                ...revisedProjectTotal,
                forecastHours: finalPlannedEffort.toNumber(),
                actualHours: finalActualHours.toNumber(),
                forecastHoursDisplay: formatHoursDisplay(finalPlannedEffort.toNumber()),
                actualHoursDisplay: formatHoursDisplay(finalActualHours.toNumber()),
                variance: roundValue(revisedProjectTotalVariance, 0),
                utilisationRate: roundValue(revisedProjectTotalUtilisationRate, 0),
                effortCompletionPercentageValue: revisedProjectTotalEffortCompletionPercentage,
                effortCompletionPercentage: `${revisedProjectTotalEffortCompletionPercentage.toFixed(2)}%`,
            };

            // Recalculate percentageComplete based on Financials (Total Claimed / Revised Contract)
            // This ensures Section-level claims are captured even if task percentages aren't updated
            if (toDecimal(revisedProjectTotal.totalSaleAmount).gt(0)) {
                const financialPercentage = roundValue(
                    toDecimal(totalClaimAmount).dividedBy(toDecimal(revisedProjectTotal.totalSaleAmount)).times(100)
                );
                revisedProjectTotal.percentageComplete = financialPercentage;
                revisedProjectTotal.completionPercentage = `${roundValue(financialPercentage, 0)}%`;
            }

            // Also update stage-level percentageComplete if it's 0 but there are claims
            // This fixes the issue where an Attributed Section has claims but 0% task completion
            if (progressClaims && Array.isArray(progressClaims)) {
                const sectionClaims = {};

                // 1. Build a taskId -> stageId mapping from sectionData for task-level claims
                // This is needed to aggregate historical task-level claims (made before attribution level was set)
                const taskToStageMap = {};
                const attributionLevelStages = new Set();
                const stageChildrenMap = {}; // stageId -> [childStageIds]

                const buildTaskStageMapping = (stagesArray, parentId = null) => {
                    stagesArray.forEach(stage => {
                        // Track attribution level stages
                        if (stage.is_attribution_level) {
                            attributionLevelStages.add(stage.id);
                        }

                        // Build parent -> children map for rolling up claims
                        if (parentId) {
                            if (!stageChildrenMap[parentId]) {
                                stageChildrenMap[parentId] = [];
                            }
                            stageChildrenMap[parentId].push(stage.id);
                        }

                        if (stage.stages && Array.isArray(stage.stages)) {
                            // Nested stages - recurse
                            buildTaskStageMapping(stage.stages, stage.id);
                        } else if (stage.tasks && Array.isArray(stage.tasks)) {
                            // Map each task to its stage
                            stage.tasks.forEach(task => {
                                if (task.id) {
                                    taskToStageMap[task.id] = stage.id;
                                }
                            });
                        }
                    });
                };
                buildTaskStageMapping(sectionData);

                // 2. Build parent-child relationships to find attribution-level ancestors
                const stageParentMap = {};
                const buildParentMap = (stagesArray, parentId = null) => {
                    stagesArray.forEach(stage => {
                        if (parentId) {
                            stageParentMap[stage.id] = parentId;
                        }
                        if (stage.stages && Array.isArray(stage.stages)) {
                            buildParentMap(stage.stages, stage.id);
                        }
                    });
                };
                buildParentMap(sectionData);

                // 3. Helper to get all ancestors for a given stageId (including the stage itself)
                const getAllAncestors = (stageId) => {
                    const ancestors = [stageId];
                    let currentId = stageId;
                    while (stageParentMap[currentId]) {
                        ancestors.push(stageParentMap[currentId]);
                        currentId = stageParentMap[currentId];
                    }
                    return ancestors;
                };

                // 4. Aggregate claims by stageId - each claim contributes to its stage AND all ancestors
                progressClaims.forEach(claim => {
                    // Use 'progressClaimTask' as defined in backend service alias, fallback to legacy if needed
                    const tasks = claim.progressClaimTask || claim.tbl_progress_claim_tasks;
                    if (claim.progressClaimStatus && claim.progressClaimStatus != 'Draft' && Array.isArray(tasks)) {
                        // Protections against double counting within a single claim
                        const stagesWithSectionClaims = new Set(
                            tasks.filter(t => !t.isDelete && t.type === 'SECTION').map(t => t.stageId)
                        );

                        tasks.filter(t => !t.isDelete).forEach(taskClaim => {
                            let targetStageId = null;

                            if (taskClaim.stageId) {
                                // Section-level claim - use stageId directly
                                targetStageId = taskClaim.stageId;
                            } else if (taskClaim.taskId) {
                                // Task-level claim - find the stage this task belongs to
                                targetStageId = taskToStageMap[taskClaim.taskId];
                            }

                            if (targetStageId) {
                                // Accuracy check: if this is a task claim, only count it if its stage doesn't have a section claim
                                if (taskClaim.type === 'SECTION' || !stagesWithSectionClaims.has(taskClaim.stageId)) {
                                    // Add claim amount to this stage AND all its ancestors
                                    const claimAmount = parseFloat(taskClaim.total) || 0;
                                    const ancestors = getAllAncestors(targetStageId);
                                    ancestors.forEach(ancestorId => {
                                        sectionClaims[ancestorId] = (sectionClaims[ancestorId] || 0) + claimAmount;
                                    });
                                }
                            }
                        });
                    }
                });

                // 5. Update ALL section percentages using financial completion (claim / sale)
                // This ensures consistency with the Budget page which uses the same calculation
                if (stageData.sections) {
                    Object.keys(stageData.sections).forEach(sectionId => {
                        const section = stageData.sections[sectionId];
                        const claimTotal = sectionClaims[sectionId] || 0;

                        // Always use financial percentage for accurate completion tracking
                        // This handles: attribution sections, parent sections with nested attribution children,
                        // and sections with mixed historical task claims and new section claims
                        if (section.totalSaleAmount > 0 && claimTotal > 0) {
                            const financialPercentage = Math.min(roundValue((claimTotal / section.totalSaleAmount) * 100), 100);
                            section.percentageComplete = financialPercentage;
                            // Non-nested sections render from taskList.total (see project-task-list.jsx
                            // hasNestedStages check), so this must be kept in sync too.
                            if (section.taskList && section.taskList.total) {
                                section.taskList.total.percentageComplete = financialPercentage;
                            }
                            // Propagate down to each individual task row too - otherwise the "Total"/
                            // section-level completion % is fixed but each task row (which reads its own
                            // taskList.tasks[taskId].percentageComplete, an unrelated stale field from the
                            // task record) keeps showing its old, pre-claim value even though the Budget
                            // page (which derives per-task completion live from claim data) shows 100%.
                            if (section.taskList && section.taskList.tasks) {
                                Object.values(section.taskList.tasks).forEach(task => {
                                    task.percentageComplete = financialPercentage;
                                });
                            }
                        }
                    });
                }
            }
            // --- End of additions ---

            progresscard.materialAllowance = revisedProjectTotal.materialCost;
            progresscard.resourceAllowance = revisedProjectTotal.laborCost;
            progresscard.contractorAllowance = revisedProjectTotal.subcontractorCost;

            financialCard.contract = stageData.projectTotal.totalSaleAmount;
            financialCard.variations = approvedOptionCalculation.projectTotal.totalSaleAmount;
            financialCard.revisedContract = revisedProjectTotal.totalSaleAmount;
            financialCard.claimed = totalClaimAmount;
            financialCard.retentionHeld = totalRetentionAmount;
            financialCard.balance = financialCard.revisedContract - (financialCard.claimed - financialCard.retentionHeld);


            /**
             * Calculates total contractor actual spending from work orders data.
             * Sums invoice-level claims or line-item claims across non-cancelled work orders.
             *
             * @param {Array<Object>} [data=[]] - List of work order purchase orders
             * @returns {number} Total contractor work done rounded to 2 decimal places
             */
            const getContractorWorkDone = (data = []) => {
                let totalCost = toDecimal(0);

                data.forEach(po => {
                    const status = (po.status || '').toLowerCase();
                    if (status === 'cancelled' || status === 'canceled') return;

                    const invoices = Array.isArray(po.invoice) ? po.invoice : [];

                    if (invoices.length > 0) {
                        let invTotal = toDecimal(0);
                        invoices.forEach(inv => {
                            if (!inv) return;

                            const invItems = Array.isArray(inv.items) ? inv.items : (typeof inv.items === 'string' ? JSON.parse(inv.items || '[]') : []);
                            const itemClaimTotal = invItems.reduce((sum, item) => sum.plus(toDecimal(item?.claimAmount || 0)), toDecimal(0));
                            const invoiceClaim = toDecimal(inv.invoiceClaim ?? inv.amountPaid ?? inv.approvedAmount ?? 0);

                            invTotal = invTotal.plus(itemClaimTotal.gt(0) ? itemClaimTotal : invoiceClaim);
                        });
                        totalCost = totalCost.plus(invTotal.gt(0) ? invTotal : toDecimal(po.amountClaimed || 0));
                    } else {
                        const poClaim = po.amountClaimed !== undefined && po.amountClaimed !== null
                            ? po.amountClaimed
                            : (po.invoiceClaim !== undefined && po.invoiceClaim !== null
                                ? po.invoiceClaim
                                : (po.claimAmount !== undefined && po.claimAmount !== null
                                    ? po.claimAmount
                                    : (po.amountPaid || 0)));

                        totalCost = totalCost.plus(toDecimal(poClaim || 0));
                    }
                });

                return roundValue(totalCost, 2);
            };

            let materialActualSpendingTotal = toDecimal(0);

            const variations = Array.isArray(optionList) && optionList.length > 0 ?
                optionList.filter(_ => !['REJECTED', 'CANCELLED'].includes(_.proposal_status) && !_.stageId) : [];

            variations.forEach((variation) => {
                const tasksActualBudget = calculateActualBudget(variation);
                materialActualSpendingTotal = materialActualSpendingTotal
                    .plus(toDecimal(tasksActualBudget.actualMaterialCost || 0));
            });

            const calculateStageProgress = (stagesData) => {
                if (!Array.isArray(stagesData)) return;

                stagesData.forEach((stage) => {
                    if (idx(stage, _ => _.stages.length)) {
                        calculateStageProgress(stage.stages);
                    }
                    if (idx(stage, _ => _.tasks.length)) {
                        stage.tasks.forEach((task) => {
                            const tasksActualBudget = calculateActualBudget(task);
                            materialActualSpendingTotal = materialActualSpendingTotal.plus(toDecimal(tasksActualBudget.actualMaterialCost || 0));
                        });
                    }
                    if (stage.attributedActualMaterialCost) {
                        materialActualSpendingTotal = materialActualSpendingTotal.plus(toDecimal(stage.attributedActualMaterialCost || 0));
                    }
                });
            };

            if (projectData.stages !== null) {
                processStages(stages);
                calculateStageProgress(sectionData);
                progresscard.materialActualSpending = roundValue(materialActualSpendingTotal);
                progresscard.contractorActualSpending = getContractorWorkDone(workOrdersList);
                progresscard.remainingMaterialBudget = roundValue(toDecimal(revisedProjectTotal.materialCost || 0).minus(toDecimal(progresscard.materialActualSpending)));
                progresscard.remainingResourceBudget = roundValue(toDecimal(revisedProjectTotal.laborCost || 0).minus(toDecimal(progresscard.resourceActualSpending)));
                progresscard.remainingContractorBudget = roundValue(toDecimal(revisedProjectTotal.subcontractorCost || 0).minus(toDecimal(progresscard.contractorActualSpending)));

                // Update Time Spend bar to use Revised Project Totals (Base + Variations)
                const hoursToTime = (h) => `${Math.floor(h)}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;
                progresscard.timeSpend = hoursToTime(finalActualHours.toNumber());
                progresscard.timeAllocated = hoursToTime(finalPlannedEffort.toNumber());
            } else {
                progresscard.materialActualSpending = roundValue(materialActualSpendingTotal);
                progresscard.remainingMaterialBudget = roundValue(toDecimal(revisedProjectTotal.materialCost || 0).minus(toDecimal(progresscard.materialActualSpending)));
                progresscard.contractorActualSpending = getContractorWorkDone(workOrdersList);
                progresscard.remainingContractorBudget = roundValue(toDecimal(revisedProjectTotal.subcontractorCost || 0).minus(toDecimal(progresscard.contractorActualSpending)));
            }

            dispatch(addProjectCardAction(projectcard));
            dispatch(addFinancialCardAction(financialCard));
            dispatch(addProgressCardAction(progresscard));
            dispatch(addOverviewCardsAction(overviewCards));
            dispatch(addProjectTotalAction(stageData.projectTotal));
            dispatch(addRevisedProjectTotalAction(revisedProjectTotal));
            dispatch(addSectionsAction(stageData.sections));
            dispatch(addOptionsAction(optionCalculation));
            dispatch(addSectionDataAction(enhancedSectionData));
            dispatch(addOptionDataAction(enhancedOptionData));
            dispatch(addVariationDataAction(enhancedVariationData));
            dispatch(addProjectVariationAction(enhancedProjectLevelVariation));
            dispatch(addSectionVariationAction(enhancedSectionLevelVariation));
            dispatch(addLaborDataAction(laborData || {}));
            dispatch(addProgressClaimsAction(progressClaims || []));
            dispatch(addOptionsListAction(optionList || []));
            dispatch(addResourceAllocationsAction(timesheetEntries || []));

            const projectDataWithoutStages = { ...projectData };
            delete projectDataWithoutStages.stages;

            dispatch(addProjectAction({
                projectId: projectData.id,
                projectName: projectData.name,
                projectManagerName: idx(projectData, _ => _.manager.name),
                quoteType: projectData.quoteType,
                projectDescription: projectData.description,
                customerName: projectData.customer && projectData.customer.businessName,
                hasStages: projectData.hasStages,
                projectType: projectData.type,
                projectDataType: projectData.projectType,
                level: projectData.level,
                customerLevel: projectData.customer ? projectData.customer.level : "",
                contactData: projectData.customerContact,
                projectMarginValue: projectData.profitMarginValue || "",
                status: projectData.status,
                siteAddress: projectData.address,
                projectDueDate: projectData.dateOfCreation,
                attachmentArr: projectData.attachment,
                showStageSelect,
                addressBreakdown: projectData.addressBreakdown,
                totalApprovedInvoices,
                res: projectDataWithoutStages,
            }));

            return {
                projectData: _.cloneDeep(projectData),
                progressClaims,
                optionList: _.cloneDeep(optionList || []),
                laborData,
            };
        });
    })();

    inFlightSummaryPromise = fetchPromise;
    try {
        const result = await fetchPromise;
        return result;
    } finally {
        if (inFlightSummaryKey === cacheKey) {
            inFlightSummaryPromise = null;
            inFlightSummaryKey = null;
        }
    }
};